import React from "react";

interface FormFieldProps {
  id: string;
  name: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  autoComplete?: string;
  baseFieldStyle: (field: string, hasValue: boolean) => React.CSSProperties;
  fieldTextStyle: React.CSSProperties;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  focused: string | null;
  setFocused: (field: string | null) => void;
  setAndValidate: (field: string, value: string) => void;
  setTouched: (touched: Record<string, boolean>) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  baseFieldStyle,
  fieldTextStyle,
  errors,
  touched,
  focused,
  setFocused,
  setAndValidate,
  setTouched,
}) => {
  const hasError = Boolean(errors[id] && touched[id]);
  const hasValue = Boolean(value);

  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="sr-only">
        {placeholder.replace("*", "")}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (touched[id]) setAndValidate(id, e.target.value);
        }}
        onBlur={() => {
          setTouched({ ...touched, [id]: true });
          setAndValidate(id, value);
          setFocused(focused === id ? null : focused);
        }}
        onFocus={() => setFocused(id)}
        required={required}
        placeholder={placeholder}
        className={`w-full rounded border bg-transparent px-4 py-3 text-white placeholder-white/60 outline-none focus:border-white/60 no-placeholder-shadow h-[46px] md:h-auto`}
        style={{
          ...baseFieldStyle(id, hasValue),
          fontSize: '16px'
        }}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${id}-error` : undefined}
      />
      {hasError && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-white mt-1"
          style={{
            ...fieldTextStyle,
            fontSize: 14,
            lineHeight: "140%",
            textShadow:
              "0px 0px 11.96px rgba(255,0,0,0.5), 0px 0px 23.92px #FF0000",
          }}
        >
          * {errors[id]}
        </p>
      )}
    </div>
  );
};

export default FormField;
